import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { FileDown, Mail, Linkedin, ChevronDown, Menu, X, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [language, setLanguage] = useState('en'); // 'en' or 'fr'
  const [showContactMenu, setShowContactMenu] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Detect active section with better accuracy
      const sections = ['about', 'skills', 'projects', 'contact'];
      const scrollPosition = window.scrollY + window.innerHeight / 3;
      
      let currentSection = '';
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            currentSection = section;
            break;
          }
        }
      }
      
      // Default to first section if at top of page
      if (window.scrollY < 300) {
        currentSection = '';
      }
      
      setActiveSection(currentSection);
    };
    
    handleScroll(); // Initial call
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  // Translations
  const translations = {
    en: {
      nav: {
        about: 'About',
        skills: 'Skills',
        projects: 'Projects',
        contact: 'Contact'
      },
      hero: {
        badge: 'MECHANICAL ENGINEER',
        title: 'Zoubeir Magham',
        subtitle: 'Structural Analysis & Design Specialist',
        description: 'Mechanical Engineer specialized in structural calculations, numerical simulation, and mechanical design. Driving innovation through FEM analysis, Python automation, and advanced engineering solutions.',
        btnContact: 'Get In Touch',
        btnProjects: 'View Projects',
        statsYears: 'Years Experience',
        statsTools: 'Tools Mastered',
        statsProjects: 'Projects Completed'
      },
      about: {
        badge: 'ABOUT ME',
        title: 'R&D Mechanical Engineer',
        p1: 'Currently serving as <strong>Vice Manager at CWS</strong>, I bring extensive experience in structural analysis, numerical simulations, and mechanical design.',
        p2: 'Skilled in analytical and finite element methods, material behavior analysis, and validation according to international engineering standards.',
        p3: 'Experienced in team coordination with a rigorous and results-oriented approach. Specialized in automation of simulations using Python scripting to optimize calculation workflows and drive continuous improvement.'
      },
      skills: {
        badge: 'TECHNICAL SKILLS',
        title: 'Expertise Areas',
        categories: {
          analysis: 'Mechanical Analysis',
          methods: 'Calculation Methods',
          materials: 'Materials',
          design: 'Mechanical Design'
        },
        items: {
          analysis: [
            'Static structural analysis',
            'Fatigue analysis',
            'Buckling and stability',
            'Structural dynamics',
            'Modal analysis',
            'Thermal analysis'
          ],
          methods: [
            'Analytical calculations',
            'Finite Element Method (FEM)',
            'Development of calculation methodologies',
            'Model validation and optimization'
          ],
          materials: [
            'Homogeneous materials',
            'Composite materials',
            'Mechanical behavior of materials',
            'Material selection and sizing'
          ],
          design: [
            'Mechanical design and dimensioning',
            '3D CAD modeling',
            'Design optimization',
            'Integration of industrial constraints'
          ]
        }
      },
      tools: {
        badge: 'TOOLS & SOFTWARE',
        title: 'Technical Stack'
      },
      processes: {
        badge: 'INDUSTRIAL PROCESSES',
        title: 'Manufacturing & Industrialization',
        items: [
          'Manufacturing processes',
          'Design for Manufacturing (DFM)',
          'Industrialization of mechanical components',
          'Interface between design office and production'
        ]
      },
      management: {
        badge: 'MANAGEMENT & STANDARDS',
        title: 'Leadership & Compliance',
        items: [
          'Technical team management (Vice Manager)',
          'Task coordination and technical supervision',
          'Application of classification society standards',
          'Compliance with Bureau Veritas (BV) regulations',
          'Continuous improvement of methods and tools'
        ]
      },
      projects: {
        badge: 'SELECTED WORK',
        title: 'Projects & Experience',
        items: [
          {
            title: 'Design of Ship Sail Stoppers',
            subtitle: 'Structural & Mechanical Design',
            description: 'Designed mechanical stoppers for ship sails, ensuring secure load transfer between the sail system and vessel structure under extreme operating conditions.',
            details: [
              'Structural design and sizing under high tensile and dynamic loads',
              '3D CAD models and detailed manufacturing drawings',
              'FEA analysis for stress levels, deformation, and safety factors',
              'Fatigue behavior evaluation under cyclic loading',
              'Compliance with marine structural and classification standards'
            ],
            tools: 'SolidWorks · Abaqus · Structural Analysis · Fatigue Analysis'
          },
          {
            title: 'Structural Truss Design',
            subtitle: 'Load-Bearing Applications',
            description: 'Designed a load-bearing truss structure for supporting critical components and transferring loads efficiently to the main frame.',
            details: [
              'Defined structural architecture and load paths',
              'Analytical sizing and FEM validation (buckling, stress, modal analysis)',
              'Weight-to-strength ratio optimization',
              'Welded and bolted connections per mechanical standards',
              'Complete design documentation and manufacturing drawings'
            ],
            tools: 'Structural Design · Abaqus · Buckling & Modal Analysis · SolidWorks'
          },
          {
            title: 'Technical Supervision & Management',
            subtitle: 'External Engineering Teams',
            description: 'Led technical follow-up and coordination of external engineering service providers for mechanical design and structural analysis activities.',
            details: [
              'Defined technical specifications and design requirements',
              'Reviewed and validated mechanical designs and calculation reports',
              'Ensured compliance with timelines and quality standards',
              'Main technical interface between internal teams and contractors',
              'Risk identification and technical issue resolution'
            ],
            tools: 'Technical Leadership · Project Management · Design Review · Supplier Management'
          },
          {
            title: 'Composite Panels & Parts Design',
            subtitle: 'Lightweight Structures',
            description: 'Designed composite panels and structural components for lightweight and high-performance mechanical applications.',
            details: [
              'Composite parts design based on functional requirements',
              'Laminate stacking sequences and material selection',
              'Structural analysis for stiffness, strength, and failure modes',
              'Weight reduction and manufacturability optimization',
              'Detailed CAD models and technical drawings for production'
            ],
            tools: 'Composite Design · Structural Analysis · SolidWorks · Material Selection'
          }
        ]
      },
      education: {
        badge: 'EDUCATION',
        title: 'Academic Background',
        master: 'Master 2 - Mechanical Design',
        licence: 'Licence in Mechanics',
        prepa: 'Preparatory Class (MPSI/MP)'
      },
      contact: {
        badge: 'GET IN TOUCH',
        title: "Let's Work Together",
        description: 'Interested in collaboration or want to discuss structural analysis projects? Feel free to reach out.',
        email: 'Email',
        linkedin: 'LinkedIn',
        whatsapp: 'WhatsApp',
        downloadCV: 'Download CV'
      },
      footer: {
        role: 'Mechanical Engineer - Structural Analysis & Design',
        rights: 'All rights reserved.'
      }
    },
    fr: {
      nav: {
        about: 'À propos',
        skills: 'Compétences',
        projects: 'Projets',
        contact: 'Contact'
      },
      hero: {
        badge: 'INGÉNIEUR MÉCANIQUE',
        title: 'Zoubeir Magham',
        subtitle: 'Spécialiste Analyse Structurale & Conception',
        description: 'Ingénieur mécanique spécialisé en calculs de structures, simulation numérique et conception mécanique. Innovation par analyse FEM, automatisation Python et solutions d\'ingénierie avancées.',
        btnContact: 'Me Contacter',
        btnProjects: 'Voir les Projets',
        statsYears: 'Années d\'Expérience',
        statsTools: 'Outils Maîtrisés',
        statsProjects: 'Projets Réalisés'
      },
      about: {
        badge: 'À PROPOS',
        title: 'Ingénieur R&D Mécanique',
        p1: 'Actuellement en poste de <strong>Vice Manager chez CWS</strong>, j\'apporte une expérience approfondie en analyse structurale, simulations numériques et conception mécanique.',
        p2: 'Compétent en méthodes analytiques et éléments finis, analyse du comportement des matériaux et validation selon les normes internationales d\'ingénierie.',
        p3: 'Expérience en coordination d\'équipes avec une approche rigoureuse et orientée résultats. Spécialisé dans l\'automatisation des simulations via scripts Python pour optimiser les flux de calcul et l\'amélioration continue.'
      },
      skills: {
        badge: 'COMPÉTENCES TECHNIQUES',
        title: 'Domaines d\'Expertise',
        categories: {
          analysis: 'Analyse Mécanique',
          methods: 'Méthodes de Calcul',
          materials: 'Matériaux',
          design: 'Conception Mécanique'
        },
        items: {
          analysis: [
            'Analyse structurale statique',
            'Analyse de fatigue',
            'Flambage et stabilité',
            'Dynamique des structures',
            'Analyse modale',
            'Analyse thermique'
          ],
          methods: [
            'Calculs analytiques',
            'Méthode des Éléments Finis (MEF)',
            'Développement de méthodologies de calcul',
            'Validation et optimisation de modèles'
          ],
          materials: [
            'Matériaux homogènes',
            'Matériaux composites',
            'Comportement mécanique des matériaux',
            'Sélection et dimensionnement des matériaux'
          ],
          design: [
            'Conception et dimensionnement mécaniques',
            'Modélisation 3D CAO',
            'Optimisation de conception',
            'Intégration de contraintes industrielles'
          ]
        }
      },
      tools: {
        badge: 'OUTILS & LOGICIELS',
        title: 'Stack Technique'
      },
      processes: {
        badge: 'PROCESSUS INDUSTRIELS',
        title: 'Fabrication & Industrialisation',
        items: [
          'Procédés de fabrication',
          'Conception pour la Fabrication (DFM)',
          'Industrialisation de composants mécaniques',
          'Interface entre bureau d\'études et production'
        ]
      },
      management: {
        badge: 'MANAGEMENT & NORMES',
        title: 'Leadership & Conformité',
        items: [
          'Management d\'équipe technique (Vice Manager)',
          'Coordination et supervision technique',
          'Application des normes de sociétés de classification',
          'Conformité aux réglementations Bureau Veritas (BV)',
          'Amélioration continue des méthodes et outils'
        ]
      },
      projects: {
        badge: 'TRAVAUX SÉLECTIONNÉS',
        title: 'Projets & Expérience',
        items: [
          {
            title: 'Conception de Stoppeurs de Voile de Navire',
            subtitle: 'Conception Structurale & Mécanique',
            description: 'Conception de stoppeurs mécaniques pour voiles de navire, assurant un transfert de charge sécurisé entre le système de voile et la structure du navire dans des conditions extrêmes.',
            details: [
              'Conception et dimensionnement structurel sous charges de traction et dynamiques élevées',
              'Modèles CAO 3D et plans de fabrication détaillés',
              'Analyse FEM pour niveaux de contrainte, déformation et facteurs de sécurité',
              'Évaluation du comportement en fatigue sous chargement cyclique',
              'Conformité aux normes structurelles marines et de sociétés de classification'
            ],
            tools: 'SolidWorks · Abaqus · Analyse Structurale · Analyse de Fatigue'
          },
          {
            title: 'Conception de Structure Treillis',
            subtitle: 'Applications Portantes',
            description: 'Conception d\'une structure treillis porteuse pour supporter des composants critiques et transférer les charges efficacement vers le châssis principal.',
            details: [
              'Définition de l\'architecture structurelle et des chemins de charge',
              'Dimensionnement analytique et validation FEM (flambage, contrainte, analyse modale)',
              'Optimisation du rapport poids/résistance',
              'Connexions soudées et boulonnées selon normes mécaniques',
              'Documentation complète de conception et plans de fabrication'
            ],
            tools: 'Conception Structurale · Abaqus · Analyse de Flambage & Modale · SolidWorks'
          },
          {
            title: 'Supervision Technique & Management',
            subtitle: 'Équipes d\'Ingénierie Externes',
            description: 'Direction du suivi technique et coordination de prestataires d\'ingénierie externes pour activités de conception mécanique et d\'analyse structurale.',
            details: [
              'Définition des spécifications techniques et exigences de conception',
              'Révision et validation des conceptions mécaniques et rapports de calcul',
              'Assurance de conformité aux délais et normes de qualité',
              'Interface technique principale entre équipes internes et sous-traitants',
              'Identification des risques et résolution de problèmes techniques'
            ],
            tools: 'Leadership Technique · Gestion de Projet · Revue de Conception · Management Fournisseurs'
          },
          {
            title: 'Conception de Panneaux & Pièces Composites',
            subtitle: 'Structures Légères',
            description: 'Conception de panneaux composites et composants structurels pour applications mécaniques légères et hautes performances.',
            details: [
              'Conception de pièces composites basée sur exigences fonctionnelles',
              'Séquences d\'empilement de stratifiés et sélection de matériaux',
              'Analyse structurale pour rigidité, résistance et modes de défaillance',
              'Optimisation de réduction de poids et fabricabilité',
              'Modèles CAO détaillés et plans techniques pour production'
            ],
            tools: 'Conception Composite · Analyse Structurale · SolidWorks · Sélection de Matériaux'
          }
        ]
      },
      education: {
        badge: 'FORMATION',
        title: 'Parcours Académique',
        master: 'Master 2 - Conception Mécanique',
        licence: 'Licence en Mécanique',
        prepa: 'Classe Préparatoire (MPSI/MP)'
      },
      contact: {
        badge: 'CONTACTEZ-MOI',
        title: 'Travaillons Ensemble',
        description: 'Intéressé par une collaboration ou souhaitez discuter de projets d\'analyse structurale ? N\'hésitez pas à me contacter.',
        email: 'Email',
        linkedin: 'LinkedIn',
        whatsapp: 'WhatsApp',
        downloadCV: 'Télécharger CV'
      },
      footer: {
        role: 'Ingénieur Mécanique - Analyse Structurale & Conception',
        rights: 'Tous droits réservés.'
      }
    }
  };

  const t = translations[language];

  const skills = [
    {
      categoryKey: 'analysis',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      categoryKey: 'methods',
      color: 'from-teal-500 to-emerald-500'
    },
    {
      categoryKey: 'materials',
      color: 'from-indigo-500 to-purple-500'
    },
    {
      categoryKey: 'design',
      color: 'from-violet-500 to-pink-500'
    }
  ];

  const tools = [
    { 
      name: 'Abaqus', 
      description: 'FEM Simulations', 
      logo: 'https://customer-assets.emergentagent.com/job_mechdesign-portfolio/artifacts/le8pe5oi_images%20%281%29.jfif',
      borderColor: 'border-blue-500',
      isEmoji: false
    },
    { 
      name: 'Ansys', 
      description: 'FEM Analysis', 
      logo: 'https://customer-assets.emergentagent.com/job_mechdesign-portfolio/artifacts/raaookcx_ANSYS_logo_without-blur.png',
      borderColor: 'border-orange-500',
      isEmoji: false
    },
    { 
      name: 'Fe-Safe', 
      description: 'Fatigue Analysis', 
      logo: 'https://customer-assets.emergentagent.com/job_mechdesign-portfolio/artifacts/wmjxfm3t_fesafe-titleimage1.webp',
      borderColor: 'border-cyan-500',
      isEmoji: false
    },
    { 
      name: 'SolidWorks', 
      description: '3D CAD Modeling', 
      logo: 'https://customer-assets.emergentagent.com/job_mechdesign-portfolio/artifacts/44i9vlq1_solidworks.png',
      borderColor: 'border-red-500',
      isEmoji: false
    },
    { 
      name: 'CATIA', 
      description: '3D Design', 
      logo: 'https://customer-assets.emergentagent.com/job_mechdesign-portfolio/artifacts/bipcx6rf_CATIA_Logotype_RGB_Blue.png',
      borderColor: 'border-purple-500',
      isEmoji: false
    },
    { 
      name: 'Python', 
      description: 'Automation & Post-processing', 
      logo: 'https://customer-assets.emergentagent.com/job_mechdesign-portfolio/artifacts/1e1evcrs_images.jfif',
      borderColor: 'border-yellow-500',
      isEmoji: false
    },
  ];

  const projectsData = [
    {
      color: 'bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200',
      image: 'https://customer-assets.emergentagent.com/job_mechdesign-portfolio/artifacts/5mrnrevb_Gemini_Generated_Image_gm30isgm30isgm30.png'
    },
    {
      color: 'bg-gradient-to-br from-teal-50 to-emerald-50 border-teal-200',
      image: 'https://customer-assets.emergentagent.com/job_mechdesign-portfolio/artifacts/2ld7cn3e_Gemini_Generated_Image_bot0p2bot0p2bot0.png'
    },
    {
      color: 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200',
      image: 'https://customer-assets.emergentagent.com/job_mechdesign-portfolio/artifacts/p6x1b1m8_Gemini_Generated_Image_kdhub4kdhub4kdhu.png'
    },
    {
      color: 'bg-gradient-to-br from-violet-50 to-pink-50 border-violet-200',
      image: 'https://customer-assets.emergentagent.com/job_mechdesign-portfolio/artifacts/wl2h7b7t_Gemini_Generated_Image_ghwm1ghwm1ghwm1g.png'
    }
  ];

  return (
    <div className="portfolio-container">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass-nav shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent"
            >
              ZOUBEIR MAGHAM
            </motion.div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-2">
              <button 
                onClick={() => scrollToSection('about')} 
                className={`nav-link-modern transition-all duration-300 ${
                  activeSection === 'about' 
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg' 
                    : 'text-gray-700'
                }`}
              >
                {t.nav.about}
              </button>
              <button 
                onClick={() => scrollToSection('skills')} 
                className={`nav-link-modern transition-all duration-300 ${
                  activeSection === 'skills' 
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg' 
                    : 'text-gray-700'
                }`}
              >
                {t.nav.skills}
              </button>
              <button 
                onClick={() => scrollToSection('projects')} 
                className={`nav-link-modern transition-all duration-300 ${
                  activeSection === 'projects' 
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg' 
                    : 'text-gray-700'
                }`}
              >
                {t.nav.projects}
              </button>
              <button 
                onClick={() => scrollToSection('contact')} 
                className={`btn-nav-cta ${activeSection === 'contact' ? 'ring-2 ring-blue-300' : ''}`}
              >
                {t.nav.contact}
              </button>
              
              {/* Language Toggle Switch */}
              <div className="flex items-center space-x-2">
                <span className={`text-sm font-medium transition-colors ${language === 'en' ? 'text-gray-900' : 'text-gray-400'}`}>EN</span>
                <button
                  onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
                  className={`relative w-14 h-7 rounded-full transition-all duration-300 ${
                    language === 'en' ? 'bg-gray-300' : 'bg-gradient-to-r from-blue-500 to-cyan-500'
                  }`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${
                    language === 'en' ? 'translate-x-0' : 'translate-x-7'
                  }`}></div>
                </button>
                <span className={`text-sm font-medium transition-colors ${language === 'fr' ? 'text-gray-900' : 'text-gray-400'}`}>FR</span>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden glass-nav border-t border-gray-200"
          >
            <div className="px-6 py-4 space-y-4">
              <button onClick={() => scrollToSection('about')} className="block w-full text-left nav-link-modern">About</button>
              <button onClick={() => scrollToSection('skills')} className="block w-full text-left nav-link-modern">Skills</button>
              <button onClick={() => scrollToSection('projects')} className="block w-full text-left nav-link-modern">Projects</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left nav-link-modern">Contact</button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-section-modern">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-5xl mx-auto"
          >
            <div className="inline-block mb-6">
              <span className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-semibold rounded-full tracking-wide">
                {t.hero.badge}
              </span>
            </div>
            
            <h1 className="hero-title-modern mb-6">
              <span className="bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 bg-clip-text text-transparent">
                {t.hero.title}
              </span>
            </h1>
            
            <h2 className="text-3xl lg:text-4xl font-light text-gray-700 mb-6 leading-relaxed">
              {t.hero.subtitle}
            </h2>
            
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
              {t.hero.description}
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                onClick={() => scrollToSection('contact')}
                className="btn-primary-modern"
              >
                {t.hero.btnContact}
              </Button>
              <Button
                onClick={() => scrollToSection('projects')}
                className="btn-secondary-modern"
              >
                {t.hero.btnProjects}
              </Button>
            </div>

            {/* Animated Stats */}
            <div className="grid grid-cols-3 gap-6 mt-16 max-w-3xl mx-auto">
              <div className="stat-card">
                <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">4+</div>
                <div className="text-sm text-gray-600 mt-2">{t.hero.statsYears}</div>
              </div>
              <div className="stat-card">
                <div className="text-4xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">6+</div>
                <div className="text-sm text-gray-600 mt-2">{t.hero.statsTools}</div>
              </div>
              <div className="stat-card">
                <div className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">15+</div>
                <div className="text-sm text-gray-600 mt-2">{t.hero.statsProjects}</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          >
            <ChevronDown className="animate-bounce text-gray-400" size={32} />
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-padding bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <span className="px-4 py-2 bg-white/80 backdrop-blur-sm text-blue-600 text-sm font-semibold rounded-full tracking-wide">
                {t.about.badge}
              </span>
            </div>
            
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl lg:text-5xl font-bold text-center mb-12">
                <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  {t.about.title}
                </span>
              </h2>
              
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Image */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex justify-center"
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-3xl transform rotate-3"></div>
                    <img
                      src="https://customer-assets.emergentagent.com/job_mechdesign-portfolio/artifacts/c69e5nev_IMG_0287.JPG"
                      alt="Zoubeir Magham"
                      className="relative w-full max-w-sm h-auto object-cover rounded-3xl shadow-2xl border-4 border-white"
                    />
                  </div>
                </motion.div>

                {/* Content */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 lg:p-10 shadow-xl border border-white/20">
                  <p className="text-lg text-gray-700 leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: t.about.p1 }}></p>
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">{t.about.p2}</p>
                  <p className="text-lg text-gray-700 leading-relaxed">{t.about.p3}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <span className="px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-600 text-sm font-semibold rounded-full tracking-wide">
                {t.skills.badge}
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold mt-6 mb-4">
                <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  {t.skills.title}
                </span>
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {skills.map((skill, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="skill-card-modern group">
                    <CardContent className="p-8">
                      <div className={`inline-block px-4 py-1 bg-gradient-to-r ${skill.color} text-white text-xs font-bold rounded-full mb-4`}>
                        {String(index + 1).padStart(2, '0')}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">
                        {t.skills.categories[skill.categoryKey]}
                      </h3>
                      <ul className="space-y-3">
                        {t.skills.items[skill.categoryKey].map((item, i) => (
                          <li key={i} className="text-gray-600 flex items-start group-hover:translate-x-2 transition-transform duration-200">
                            <span className={`inline-block w-2 h-2 rounded-full bg-gradient-to-r ${skill.color} mr-3 mt-2 flex-shrink-0`}></span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tools Section */}
      <section className="section-padding bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <span className="px-4 py-2 bg-white/80 backdrop-blur-sm text-indigo-600 text-sm font-semibold rounded-full tracking-wide">
                {t.tools.badge}
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold mt-6 mb-4">
                <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  {t.tools.title}
                </span>
              </h2>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6">
              {tools.map((tool, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="tool-card-modern group"
                >
                  <div className="text-center">
                    <div className={`w-20 h-20 bg-white border-4 ${tool.borderColor} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg overflow-hidden p-2`}>
                      <img 
                        src={tool.logo} 
                        alt={tool.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{tool.name}</h3>
                    <p className="text-sm text-gray-600">{tool.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industrial Processes & Management */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 lg:p-10 border-2 border-teal-200"
            >
              <span className="px-4 py-2 bg-teal-500 text-white text-xs font-bold rounded-full tracking-wide">
                {t.processes.badge}
              </span>
              <h3 className="text-3xl font-bold text-gray-900 mt-6 mb-6">{t.processes.title}</h3>
              <ul className="space-y-3">
                {t.processes.items.map((item, index) => (
                  <li key={index} className="text-gray-700 flex items-start">
                    <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 mr-3 mt-2 flex-shrink-0"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 lg:p-10 border-2 border-indigo-200"
            >
              <span className="px-4 py-2 bg-indigo-500 text-white text-xs font-bold rounded-full tracking-wide">
                {t.management.badge}
              </span>
              <h3 className="text-3xl font-bold text-gray-900 mt-6 mb-6">{t.management.title}</h3>
              <ul className="space-y-3">
                {t.management.items.map((item, index) => (
                  <li key={index} className="text-gray-700 flex items-start">
                    <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 mr-3 mt-2 flex-shrink-0"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="section-padding bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <span className="px-4 py-2 bg-white/80 backdrop-blur-sm text-blue-600 text-sm font-semibold rounded-full tracking-wide">
                {t.projects.badge}
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold mt-6 mb-4">
                <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  {t.projects.title}
                </span>
              </h2>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-6">
              {t.projects.items.map((project, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className={`project-card-modern ${projectsData[index].color} group`}>
                    <CardContent className="p-0">
                      {/* Project Image */}
                      {projectsData[index].image && (
                        <div className="w-full h-48 overflow-hidden rounded-t-2xl">
                          <img 
                            src={projectsData[index].image} 
                            alt={project.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      
                      <div className="p-8">
                        <div className="flex items-center justify-between mb-4">
                          <div className="text-xs font-bold text-gray-500 tracking-wider">
                            PROJECT {String(index + 1).padStart(2, '0')}
                          </div>
                          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                            <ChevronDown className="transform -rotate-90 text-gray-600" size={20} />
                          </div>
                        </div>
                        
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{project.title}</h3>
                        {project.subtitle && (
                          <p className="text-sm font-semibold text-blue-600 mb-4">{project.subtitle}</p>
                        )}
                        
                        <p className="text-gray-700 leading-relaxed mb-4">{project.description}</p>
                        
                        {project.details && (
                          <div className="mb-4">
                            <h4 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wide">
                              {language === 'en' ? 'Key Contributions' : 'Contributions Clés'}
                            </h4>
                            <ul className="space-y-2">
                              {project.details.map((detail, i) => (
                                <li key={i} className="text-sm text-gray-600 flex items-start">
                                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 mr-2 mt-1.5 flex-shrink-0"></span>
                                  <span>{detail}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {project.tools && (
                          <div className="pt-4 border-t border-gray-200">
                            <p className="text-xs font-semibold text-gray-500 mb-1">
                              {language === 'en' ? 'Tools & Methods' : 'Outils & Méthodes'}
                            </p>
                            <p className="text-sm text-gray-700">{project.tools}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Education Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <span className="px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-600 text-sm font-semibold rounded-full tracking-wide">
                {t.education.badge}
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold mt-6 mb-4">
                <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  {t.education.title}
                </span>
              </h2>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <Card className="education-card-modern">
                <CardContent className="p-8 lg:p-12">
                  <div className="space-y-8">
                    <div className="border-l-4 border-blue-500 pl-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.education.master}</h3>
                      <p className="text-lg text-blue-600 font-semibold">Université Paris Saclay</p>
                      <p className="text-sm text-gray-500 mt-2 font-mono">2021 - 2022</p>
                    </div>
                    <div className="border-l-4 border-cyan-500 pl-6">
                      <h4 className="text-xl font-bold text-gray-900 mb-2">{t.education.licence}</h4>
                      <p className="text-lg text-cyan-600 font-semibold">UFR Le Havre</p>
                      <p className="text-sm text-gray-500 mt-2 font-mono">2018 - 2020</p>
                    </div>
                    <div className="border-l-4 border-teal-500 pl-6">
                      <h4 className="text-xl font-bold text-gray-900 mb-2">{t.education.prepa}</h4>
                      <p className="text-lg text-teal-600 font-semibold">Polytechnique de Nouakchott & Esprit Prepa</p>
                      <p className="text-sm text-gray-500 mt-2 font-mono">2016 - 2018</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <span className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold rounded-full tracking-wide">
                {t.contact.badge}
              </span>
              <h2 className="text-4xl lg:text-6xl font-bold mt-6 mb-6">
                {t.contact.title}
              </h2>
              <p className="text-xl text-white/90 max-w-2xl mx-auto">
                {t.contact.description}
              </p>
            </div>
            
            <div className="max-w-3xl mx-auto">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <a
                  href="mailto:zoubeir.magham@gmail.com"
                  className="contact-card-modern group"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-xl">
                    <svg className="w-9 h-9" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z"/>
                    </svg>
                  </div>
                  <div className="text-sm font-bold text-gray-900 mb-2">{t.contact.email}</div>
                  <div className="text-xs text-gray-600">zoubeir.magham@gmail.com</div>
                </a>
                
                <a
                  href="http://www.linkedin.com/in/zoubeir-magham"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-card-modern group"
                >
                  <div className="w-16 h-16 bg-[#0A66C2] rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-xl">
                    <svg className="w-9 h-9" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </div>
                  <div className="text-sm font-bold text-gray-900 mb-2">{t.contact.linkedin}</div>
                  <div className="text-xs text-gray-600">zoubeir-magham</div>
                </a>

                <a
                  href="https://wa.me/33636383752"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-card-modern group"
                >
                  <div className="w-16 h-16 bg-[#25D366] rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-xl">
                    <svg className="w-10 h-10" viewBox="0 0 48 48" fill="white" xmlns="http://www.w3.org/2000/svg">
                      <path d="M24 4C12.95 4 4 12.95 4 24c0 3.45.87 6.7 2.4 9.54L4.14 43.14l9.84-2.58A19.92 19.92 0 0024 44c11.05 0 20-8.95 20-20S35.05 4 24 4zm10.66 27.88c-.44 1.26-2.6 2.31-3.58 2.47-1 .16-1.83.72-6.18-.96-5.36-2.06-8.8-7.58-9.08-7.93-.27-.35-2.24-2.98-2.24-5.68 0-2.7 1.42-4.03 1.92-4.58.5-.55 1.1-.69 1.47-.69.37 0 .74.01 1.06.02.34.01.8-.13 1.25.95.46 1.1 1.57 3.83 1.71 4.11.14.28.23.61.05 1-.18.38-.27.62-.54.96-.27.34-.57.76-.82 1.02-.27.28-.55.58-.24 1.14.31.55 1.38 2.28 2.96 3.69 2.04 1.82 3.76 2.38 4.29 2.65.53.27.84.23 1.15-.14.31-.37 1.33-1.55 1.68-2.08.36-.53.71-.44 1.2-.26.49.17 3.12 1.47 3.66 1.74.53.27.89.4 1.02.63.13.22.13 1.29-.31 2.55z"/>
                    </svg>
                  </div>
                  <div className="text-sm font-bold text-gray-900 mb-2">{t.contact.whatsapp}</div>
                  <div className="text-xs text-gray-600">+33 6 36 38 37 52</div>
                </a>
              </div>

              <div className="text-center">
                <a
                  href="https://customer-assets.emergentagent.com/job_mechdesign-portfolio/artifacts/5e8k342h_CV%20zoubeir_1%20%282%29.pdf"
                  download="CV_Zoubeir_Magham.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="btn-download-modern">
                    <FileDown className="mr-2" size={20} />
                    {t.contact.downloadCV}
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div>
              <div className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">ZOUBEIR MAGHAM</div>
              <div className="text-sm text-gray-400 mt-1">{t.footer.role}</div>
            </div>
            <div className="text-sm text-gray-400">
              © {new Date().getFullYear()} Zoubeir Magham. {t.footer.rights}
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Contact Button */}
      <div className="fixed bottom-8 right-8 z-50">
        {/* Contact Menu */}
        {showContactMenu && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            className="absolute bottom-20 right-0 bg-white rounded-2xl shadow-2xl p-4 space-y-3 min-w-[200px]"
          >
            <a
              href="mailto:zoubeir.magham@gmail.com"
              className="flex items-center space-x-3 p-3 hover:bg-blue-50 rounded-xl transition-colors group"
            >
              <Mail className="text-blue-600 group-hover:scale-110 transition-transform" size={20} />
              <span className="text-sm font-medium text-gray-700">Email</span>
            </a>
            
            <a
              href="http://www.linkedin.com/in/zoubeir-magham"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-3 p-3 hover:bg-blue-50 rounded-xl transition-colors group"
            >
              <Linkedin className="text-blue-600 group-hover:scale-110 transition-transform" size={20} />
              <span className="text-sm font-medium text-gray-700">LinkedIn</span>
            </a>
            
            <a
              href="https://wa.me/33636383752"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-3 p-3 hover:bg-green-50 rounded-xl transition-colors group"
            >
              <MessageCircle className="text-green-600 group-hover:scale-110 transition-transform" size={20} />
              <span className="text-sm font-medium text-gray-700">WhatsApp</span>
            </a>
          </motion.div>
        )}

        {/* Main Floating Button */}
        <motion.button
          onClick={() => setShowContactMenu(!showContactMenu)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full shadow-2xl flex items-center justify-center text-white hover:shadow-3xl transition-all duration-300"
        >
          {showContactMenu ? <X size={28} /> : <MessageCircle size={28} />}
        </motion.button>
      </div>
    </div>
  );
};

export default Home;